import React, { useState, useEffect, useRef } from 'react';
import Confetti from 'react-confetti';
import ImageCarousel from '../common/ImageCarousel';
import { LoadingSpinner } from '../ui/Icons';
import LearningStatusBadge from '../ui/LearningStatusBadge';
import { getImageUrl } from '../../utils/api';
import { GraduationCap } from 'lucide-react';

// New shared components
import QuizLayout from './shared/QuizLayout';
import QuizHeader from './shared/QuizHeader';
import QuizCard from './shared/QuizCard';
import PlantInfoGrid from './shared/PlantInfoGrid';
import DifficultyButtons from './shared/DifficultyButtons';

import CompactPlantInfo from './shared/CompactPlantInfo';

const FlashcardQuizV4 = ({ deck, onFinish, onReview, trackInteraction, userPlantData, onAbort, theme }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isFlipped, setIsFlipped] = useState(false);
    const [isChanging, setIsChanging] = useState(false);
    const [showConfetti, setShowConfetti] = useState(false);
    const [results, setResults] = useState([]);
    const startTimeRef = useRef(Date.now());

    const currentCard = deck[currentIndex];
    const userCardData = userPlantData?.[currentCard?.id] || {};

    useEffect(() => {
        startTimeRef.current = Date.now();
        setIsFlipped(false);
    }, [currentIndex]);

    useEffect(() => {
        if (currentIndex < deck.length - 1) {
            const nextCard = deck[currentIndex + 1];
            if (nextCard.images?.[0]) {
                const img = new Image();
                img.src = getImageUrl(nextCard.images[0].image);
            }
        }
    }, [currentIndex, deck]);

    const handleFlip = () => {
        if (!isFlipped) {
            setIsFlipped(true);
            trackInteraction?.('QUIZ_CARD_FLIP', { plant_id: currentCard.id });
        }
    };

    const handleRating = (rating) => {
        const timeTaken = (Date.now() - startTimeRef.current) / 1000;
        let confidence = 0.5;
        let knewIt = true;

        if (rating === 'again') {
            confidence = 0.0;
            knewIt = false;
        } else if (rating === 'good') {
            confidence = 0.8;
            knewIt = true;
        } else if (rating === 'easy') {
            confidence = 1.0;
            knewIt = true;
        }

        onReview?.(currentCard, knewIt, rating, timeTaken);

        if (trackInteraction) {
            trackInteraction(knewIt ? 'FLASHCARD_CORRECT' : 'FLASHCARD_INCORRECT', {
                plant_id: currentCard.id,
                confidence: rating,
                mode: 'flashcard_v4'
            });
        }

        const updatedResults = [...results, {
            plant: currentCard,
            correct: knewIt,
            confidence: rating
        }];
        setResults(updatedResults);

        if (currentIndex < deck.length - 1) {
            setIsFlipped(false);
            // Start fading out the content just before the midpoint of the flip
            setTimeout(() => setIsChanging(true), 200);
            
            setTimeout(() => {
                setCurrentIndex(prev => prev + 1);
                // Start fading back in after the data has swapped
                setTimeout(() => setIsChanging(false), 50);
            }, 250);
        } else {
            setShowConfetti(true);
            setTimeout(() => onFinish(updatedResults), 1500);
        }
    };

    if (!currentCard) return <LoadingSpinner />;

    return (
        <QuizLayout onClose={onAbort} theme={theme} className="flex flex-col overflow-hidden">
            <div id="v4-marker" className="hidden">V4_ACTIVE</div>
            {showConfetti && <Confetti numberOfPieces={200} recycle={false} style={{ zIndex: 100 }} />}

            {/* Premium Header */}
            <QuizHeader
                current={currentIndex + 1}
                total={deck.length}
                onClose={onAbort}
                status={userCardData.learning_status}
                theme={theme}
            />

            {/* Card Area - Responsive aspect ratio */}
            <div className="flex-1 relative min-h-0 w-full flex items-center justify-center p-3 sm:p-6">
                <div
                    id="quiz-card-container"
                    className={`
                        relative w-full max-w-[460px] md:max-w-[500px] h-[90dvh] md:h-[750px] max-h-full
                        transition-all duration-500 transform-style-3d cursor-pointer
                        ${isFlipped ? 'rotate-y-180 scale-[1.02]' : 'hover:scale-[1.01]'}
                    `}
                    onClick={handleFlip}
                >
                    {/* FRONT SIDE */}
                    <div
                        id="flashcard-front"
                        style={{ backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden' }}
                        className={`
                            absolute inset-0 flex flex-col rounded-[2.5rem] overflow-hidden shadow-2xl border border-black/10 dark:border-white/10
                            ${isFlipped ? 'invisible opacity-0' : 'visible opacity-100'}
                            ${isChanging ? 'opacity-0 scale-95' : 'opacity-100 scale-100'}
                            transition-all duration-300 z-10 bg-slate-900
                        `}
                    >
                        {/* Fill Image with forced rounded-none on children to avoid double-borders */}
                        <div className="absolute inset-0 bg-slate-900 [&_div]:rounded-none [&_img]:rounded-none">
                            <ImageCarousel
                                images={currentCard.images}
                                alt={currentCard.german_name}
                                className="h-full w-full object-cover"
                                showThumbnails={false}
                            />
                        </div>
                        
                        {/* Dramatic Gradient Overlay */}
                        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/90 pointer-events-none" />

                        {/* Top Overlay */}
                        <div className="absolute top-6 left-6 z-40">
                             {currentCard.is_intermediate_exam_subject && (
                                <div className="flex items-center gap-2 px-3.5 py-1.5 bg-amber-400 backdrop-blur-xl border border-amber-300/50 rounded-xl shadow-lg">
                                    <GraduationCap className="w-4 h-4 text-amber-950" />
                                    <span className="text-[10px] font-black uppercase text-amber-950 tracking-wider">Wichtig</span>
                                </div>
                            )}
                        </div>

                        {/* Question Box at Bottom */}
                        <div className="absolute bottom-0 inset-x-0 p-8 pt-20 flex flex-col justify-end z-30">
                            <div className="text-center">
                                <h3 className={`
                                    ${currentCard.german_name.length > 30 ? 'text-2xl md:text-3xl' : 
                                      currentCard.german_name.length > 20 ? 'text-3xl md:text-4xl' : 
                                      'text-4xl md:text-5xl'} 
                                    font-black text-white leading-[1.1] mb-8 drop-shadow-[0_4px_16px_rgba(0,0,0,0.8)] italic break-words px-4
                                `}>
                                    {currentCard.german_name}
                                </h3>
                                <div className="inline-flex items-center justify-center gap-3 py-4 px-10 bg-plantlix-600/90 hover:bg-plantlix-500 backdrop-blur-md rounded-full shadow-[0_8px_32px_rgba(16,185,129,0.4)] border border-plantlix-400/30 text-white text-xs font-black uppercase tracking-widest transition-all cursor-pointer ring-4 ring-black/10">
                                    <span className="w-2 h-2 rounded-full bg-white/90 animate-pulse" />
                                    Umdrehen
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* BACK SIDE (Solution) */}
                    <div
                        id="flashcard-back"
                        style={{ backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden' }}
                        className={`
                            absolute inset-0 rotate-y-180 flex flex-col rounded-[2.5rem] overflow-hidden shadow-2xl border border-black/10 dark:border-white/10 bg-white dark:bg-slate-950
                            ${!isFlipped ? 'invisible opacity-0' : 'visible opacity-100'}
                            ${isChanging ? 'opacity-0 scale-95' : 'opacity-100 scale-100'}
                            transition-all duration-300 z-20
                        `}
                    >
                        {/* Header Image Overlay (Solution Reveal) */}
                        <div className="h-[25%] min-h-[160px] relative flex-shrink-0 bg-slate-900 overflow-hidden">
                            {currentCard.images?.[0] && (
                                <>
                                    <img
                                        src={getImageUrl(currentCard.images[0].image)}
                                        alt=""
                                        className="w-full h-full object-cover opacity-90 animate-in zoom-in-110 duration-1000"
                                    />
                                    <div className="absolute top-6 right-6 px-3 py-1.5 bg-black/60 backdrop-blur-md rounded-xl text-white/90 text-[9px] font-black tracking-widest z-40 border border-white/20 uppercase">
                                        Lösung
                                    </div>
                                </>
                            )}
                            <div className="absolute inset-0 bg-gradient-to-t from-white dark:from-slate-950 via-white/50 dark:via-slate-950/40 to-black/30 flex flex-col items-center justify-end text-center pb-4 px-6 z-30">
                                <h2 className="text-2xl md:text-3xl font-black text-gray-900 dark:text-white italic tracking-tight drop-shadow-sm dark:drop-shadow-[0_4px_16px_rgba(0,0,0,0.9)] leading-tight break-words px-6">
                                    {currentCard.botanical_name}
                                </h2>
                            </div>
                        </div>

                        {/* Content Area */}
                        <div className="flex-1 flex flex-col p-6 md:p-8 overflow-hidden bg-white dark:bg-slate-950 relative">
                            {/* Decorative background glow */}
                            <div className="absolute top-0 inset-x-0 h-32 bg-gradient-to-b from-plantlix-500/5 to-transparent pointer-events-none" />
                            
                            <div className="flex-1 min-h-0 overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-gray-200 dark:scrollbar-thumb-white/10 space-y-5 relative z-10">
                                <CompactPlantInfo plant={currentCard} theme={theme} />
                                
                                {currentCard.notes && (
                                    <div className="p-5 md:p-6 bg-plantlix-50 dark:bg-plantlix-500/10 rounded-2xl border border-plantlix-100 dark:border-plantlix-500/20 relative overflow-hidden group mt-2">
                                        <p className="text-[9px] uppercase font-black text-plantlix-600 dark:text-plantlix-400 mb-2 tracking-[0.2em]">Merkhilfe</p>
                                        <p className="text-[14px] md:text-[15px] text-gray-800 dark:text-white/90 leading-relaxed font-bold italic">
                                            "{currentCard.notes}"
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Rating Area - Premium Style */}
                        <div className="p-5 md:p-8 bg-gray-50 dark:bg-[#0B0F17] border-t border-gray-100 dark:border-white/5 relative z-20">
                            <div className="text-center mb-5">
                                <span className="text-[10px] uppercase tracking-[0.4em] font-black text-gray-500 dark:text-white/40">Wie gut wusstest du es?</span>
                            </div>
                            <DifficultyButtons compact={true} onSelect={handleRating} />
                        </div>
                    </div>
                </div>
            </div>
        </QuizLayout>
    );
};

export default FlashcardQuizV4;
